import { UITask } from '@/context/house/tasks/types.ts';
import { TaskStatus } from '@/types/tasks.ts';

export const MAX_CONCURRENT_DEFAULT = 5;
/**
 * Utility functions for working with UI tasks
 */
export const getUITaskStatus = (uiTask: UITask): TaskStatus => {
	return uiTask.execution.apiTask.status;
};
/**
 * Shown while a task is working but has reported no progress of its own. Below the
 * 10 acceptance sets, so the bar only ever moves forward: 0 → 5 → 10 → 100.
 *
 * A short retry keeps whatever it had reached rather than winding back to nothing: the transfer
 * restarts within seconds, and a bar that empties and refills over that reads as a fault rather
 * than as throttling nobody needed to know about. The retry policy zeroes progress explicitly for
 * the waits long enough to be worth showing.
 */
export const ACTIVE_TASK_MIN_PROGRESS = 5;

export const getUITaskProgress = (uiTask: UITask): number | null => {
	const status = getUITaskStatus(uiTask);
	const progress = uiTask.execution.apiTask.progress;
	switch (status) {
		case 'completed':
			return 100;
		case 'failed':
		case 'skipped':
		case 'cancelled':
		case 'paused':
			return 0; // Non-success terminal tasks have no row progress
		case 'in-progress':
		case 'pending':
			return typeof progress === 'number' && progress > 0 ? progress : ACTIVE_TASK_MIN_PROGRESS;
		case 'queued':
			// A task that has never run has no progress to keep; one waiting out a short retry does.
			return typeof progress === 'number' && progress > 0 ? progress : 0;
		default:
			return progress ?? null;
	}
};
